import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useLazyQuery } from '@apollo/client';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';
import { ReactComponent as SearchIcon } from '../../shared/icons/search-icon.svg';
import { useAuth } from '../../auth';
import { useTeamInfo } from '../../team-id-context';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { fillPlaceholders, paths, useNavigate } from '../../routing';
import { GLOBAL_SEARCH } from '../../api/queries/globalSearch';
import ContactSlideout from '../contacts/ContactSlideout';
import { CategoryPills } from './CategoryPills';
import { WorkspaceFilter } from './WorkspaceFilter';
import { SearchResults } from './SearchResults';
import { EmptyStates } from './EmptyStates';
import styles from './GlobalSearchSlideout.module.scss';

export interface SearchResultItem {
  resultType: string;
  id: string;
  title: string;
  subtitle?: string;
  status?: string;
  workspaceName?: string;
  workspaceId?: string;
  relevanceScore: number;
  updatedAt?: string;
  dealId?: string;
  documentId?: string;
  templateId?: string;
  isOrgTemplate?: boolean;
  /** Present only when the hit came from document content, not the title. */
  snippet?: string;
  clauseHeading?: string;
  clauseType?: string;
  contactData?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    company?: string;
    title?: string;
    address?: string;
    contactType: 'person' | 'company';
    customFields?: Record<string, any>;
  };
}

interface GlobalSearchSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  /** Hands a document result to the AI assistant. Search finds the document; the
   *  assistant answers questions about it. */
  onAskAi?: (item: SearchResultItem) => void;
}

type ViewMode = 'search' | 'contact-detail';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
// Matching inside bodies is noisier than matching titles, so it is opt-in and the
// choice is remembered per browser.
const CONTENT_PREF_KEY = 'propdocs.globalSearch.searchContent';

export const GlobalSearchSlideout: React.FC<GlobalSearchSlideoutProps> = ({ isOpen, onClose, onAskAi }) => {
  const [{ user }] = useAuth();
  const { teamId: currentTeamId } = useTeamInfo();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[] | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[] | null>(null);
  const [searchContent, setSearchContent] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CONTENT_PREF_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [viewMode, setViewMode] = useState<ViewMode>('search');
  const [selectedContact, setSelectedContact] = useState<SearchResultItem['contactData'] | undefined>();

  const [executeSearch, { data, loading, called }] = useLazyQuery(GLOBAL_SEARCH, {
    fetchPolicy: 'network-only',
  });

  const results: SearchResultItem[] = data?.globalSearch?.results ?? [];
  const categoryCounts: Record<string, number> = data?.globalSearch?.categoryCounts ?? {};
  const totalCount: number = data?.globalSearch?.totalCount ?? 0;

  // Focus input when slideout opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Debounced search
  const triggerSearch = useCallback(
    (
      searchQuery: string,
      category: string,
      teamIds: string[] | null,
      content: boolean,
      formats: string[] | null
    ) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (searchQuery.trim().length < MIN_CHARS) return;

      debounceTimer.current = setTimeout(() => {
        const allTeamIds = user?.allTeams?.map((t: { id: string }) => t.id) ?? [];
        executeSearch({
          variables: {
            searchQuery: searchQuery.trim(),
            teamIds: teamIds ?? allTeamIds,
            category,
            limit: 20,
            searchContent: content,
            documentFormats: formats,
          },
        });
      }, DEBOUNCE_MS);
    },
    [executeSearch, user]
  );

  const handleContentToggle = useCallback(
    (next: boolean) => {
      setSearchContent(next);
      try {
        localStorage.setItem(CONTENT_PREF_KEY, String(next));
      } catch {
        // A browser blocking site data must not break searching.
      }
      triggerSearch(query, activeCategory, selectedTeamIds, next, selectedFormats);
    },
    [query, activeCategory, selectedTeamIds, selectedFormats, triggerSearch]
  );

  const handleFormatChange = useCallback(
    (formats: string[] | null) => {
      setSelectedFormats(formats);
      triggerSearch(query, activeCategory, selectedTeamIds, searchContent, formats);
    },
    [query, activeCategory, selectedTeamIds, searchContent, triggerSearch]
  );

  const handleQueryChange = (value: string) => {
    setQuery(value);
    triggerSearch(value, activeCategory, selectedTeamIds, searchContent, selectedFormats);
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    if (query.trim().length >= MIN_CHARS) {
      triggerSearch(query, category, selectedTeamIds, searchContent, selectedFormats);
    }
  };

  const handleWorkspaceChange = (teamIds: string[] | null) => {
    setSelectedTeamIds(teamIds);
    if (query.trim().length >= MIN_CHARS) {
      triggerSearch(query, activeCategory, teamIds, searchContent, selectedFormats);
    }
  };

  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const handleResultClick = (item: SearchResultItem) => {
    // Use the item's workspace if the user belongs to it, otherwise fall back to
    // their current team. This handles received documents (workspace is the creator's
    // team), deals via deal_access_rights, and org-shared templates/clauses.
    const userTeamIds = user?.allTeams?.map((t: { id: string }) => t.id) ?? [];
    const teamId = item.workspaceId && userTeamIds.includes(item.workspaceId)
      ? item.workspaceId
      : currentTeamId;

    switch (item.resultType) {
      case 'deal':
        if (item.dealId) {
          navigate(fillPlaceholders(paths.deals.detail, { teamId, dealId: item.dealId }));
          onClose();
        }
        break;

      case 'document':
        if (item.dealId && item.documentId) {
          navigate(fillPlaceholders(paths.Documents.document, { teamId, dealId: item.dealId, documentId: item.documentId }));
          onClose();
        }
        break;

      case 'template':
        if (item.templateId) {
          const templatePath = item.isOrgTemplate ? paths.templates.orgTemplate : paths.templates.document;
          navigate(fillPlaceholders(templatePath, { teamId, templateId: item.templateId }));
          onClose();
        }
        break;

      case 'clause':
        if (item.templateId) {
          const clausePath = item.isOrgTemplate ? paths.templates.orgTemplate : paths.templates.document;
          navigate(fillPlaceholders(clausePath, { teamId, templateId: item.templateId }));
          onClose();
        }
        break;

      case 'contact':
        if (item.contactData) {
          setSelectedContact(item.contactData);
          setViewMode('contact-detail');
        }
        break;

      case 'checklist_item':
        if (item.dealId) {
          navigate(fillPlaceholders(paths.deals.detail, { teamId, dealId: item.dealId }) + '?openChecklist=true');
          onClose();
        }
        break;
    }
  };

  const handleContactClose = () => {
    setSelectedContact(undefined);
    setViewMode('search');
  };

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow close animation
      const timer = setTimeout(() => {
        setQuery('');
        setActiveCategory('all');
        setSelectedTeamIds(null);
        setViewMode('search');
        setSelectedContact(undefined);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const teams = user?.allTeams ?? [];
  const showWorkspaceFilter = teams.length > 1;

  const renderContent = () => {
    return (
      <>
        {/* Search Input */}
        <div className={styles.searchInputWrapper}>
          <div className={styles.searchInputContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              ref={inputRef}
              className={styles.searchInput}
              type="text"
              placeholder="Search deals, documents, templates..."
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              autoComplete="off"
            />
            {query && (
              <button className={styles.clearButton} onClick={handleClear} aria-label="Clear search">
                <CloseIcon />
              </button>
            )}
          </div>
          <label className={styles.contentToggle}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={searchContent}
              onChange={(e) => handleContentToggle(e.target.checked)}
            />
            <span>Search inside documents</span>
          </label>
        </div>

        {/* Category Pills + Workspace Filter */}
        <div className={styles.pillsWrapper}>
          <div className={styles.pillsRow}>
            <CategoryPills
              activeCategory={activeCategory}
              categoryCounts={categoryCounts}
              onCategoryChange={handleCategoryChange}
              selectedFormats={selectedFormats}
              onFormatChange={handleFormatChange}
            />
            {showWorkspaceFilter && (
              <WorkspaceFilter
                teams={teams}
                selectedTeamIds={selectedTeamIds}
                onWorkspaceChange={handleWorkspaceChange}
              />
            )}
          </div>
        </div>

        {/* Results or Empty States */}
        <div className={styles.resultsList}>
          {query.trim().length < MIN_CHARS ? (
            <EmptyStates type={query.length === 0 ? 'initial' : 'min-chars'} />
          ) : loading ? (
            <EmptyStates type="loading" />
          ) : called && results.length === 0 ? (
            <EmptyStates type="no-results" query={query} />
          ) : (
            <SearchResults
              results={results}
              showWorkspace={selectedTeamIds === null && teams.length > 1}
              onResultClick={handleResultClick}
              onAskAi={onAskAi}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {ReactDOM.createPortal(
        <>
          {isOpen && isMobile && <div className={styles.slideoutBackdrop} onClick={onClose} />}
          <div
            className={classNames(
              styles.slideoutWrapper,
              isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
            )}
          >
            <div className={styles.slideout}>
              <div className={styles.slideout__header}>
                <div className={styles.slideout__title}>Search</div>
                <div className={styles.slideout__close} onClick={onClose}>
                  <CloseIcon />
                </div>
              </div>
              <div className={styles.slideout__content}>{renderContent()}</div>
            </div>
          </div>
        </>,
        document.body
      )}
      {/* Contact detail slideout — renders as overlay via its own portal */}
      {selectedContact && (
        <ContactSlideout
          contact={selectedContact}
          onClose={handleContactClose}
          onSave={handleContactClose}
        />
      )}
    </>
  );

};
